import { useState, useEffect, useCallback } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { ramsAPI } from '../../../api';
import Layout from '../../../components/Layout';
import { Button } from '../../../components/ui/Button';
import { Input } from '../../../components/ui/Input';
import { Textarea } from '../../../components/ui/Textarea';
import { Select } from '../../../components/ui/Select';
import { FormLabel } from '../../../components/ui/FormLabel';
import { Card, CardContent } from '../../../components/ui/Card';
import {
  Table,
  TableHeader,
  TableBody,
  TableRow,
  TableHead,
  TableCell,
} from '../../../components/ui/Table';
import { ArrowLeft, Plus, Trash2, GitBranch, Sparkles, Wand2 } from 'lucide-react';
import RamsAISlideOver from './RamsAISlideOver';
import { Badge } from '../../../components/ui/Badge';
import { format } from 'date-fns';

const STATUS_LABELS = { draft: 'Draft', issued: 'Issued', approved: 'Approved', archived: 'Archived' };
const STATUS_TRANSITIONS = {
  draft: [
    { value: 'issued', label: 'Issue' },
    { value: 'archived', label: 'Archive' },
  ],
  issued: [
    { value: 'approved', label: 'Approve' },
    { value: 'archived', label: 'Archive' },
    { value: 'draft', label: 'Back to Draft' },
  ],
  approved: [{ value: 'archived', label: 'Archive' }],
  archived: [],
};

// Enums per plan (UK-focused RAMS)
const HAZARD_CATEGORIES = [
  'Electrical',
  'Working at height',
  'Manual handling',
  'Slips, trips and falls',
  'Fire',
  'Asbestos',
  'Noise',
  'Vibration',
  'Chemicals / hazardous substances',
  'Confined space',
  'Excavation',
  'Plant and equipment',
  'Vehicles',
  'Other',
];

const PERSONS_AT_RISK = [
  'Operatives',
  'Site staff',
  'Client / building occupants',
  'Members of the public',
  'Contractors',
  'Other',
];

const PERMIT_TYPES = [
  'Permit to work (PTW)',
  'Hot work permit',
  'Confined space permit',
  'Electrical permit',
  'Excavation permit',
  'Other',
];

const LIKELIHOOD_SEVERITY_OPTS = [
  { value: 1, label: '1' },
  { value: 2, label: '2' },
  { value: 3, label: '3' },
  { value: 4, label: '4' },
  { value: 5, label: '5' },
];

const RISK_MAX = 25;
function calcRisk(l, s) {
  if (l == null || s == null) return null;
  const L = Math.max(1, Math.min(5, Number(l)));
  const S = Math.max(1, Math.min(5, Number(s)));
  return L * S;
}

const DEFAULT_HAZARD = () => ({
  id: crypto.randomUUID?.() ?? `h-${Date.now()}-${Math.random().toString(36).slice(2)}`,
  hazardCategory: '',
  hazardText: '',
  personsAtRisk: [],
  initialLikelihood: null,
  initialSeverity: null,
  initialRisk: null,
  residualLikelihood: null,
  residualSeverity: null,
  residualRisk: null,
  controlMeasures: [],
  sortOrder: 0,
});

const DEFAULT_METHOD_STEP = () => ({
  id: crypto.randomUUID?.() ?? `s-${Date.now()}-${Math.random().toString(36).slice(2)}`,
  stepOrder: 0,
  stepTitle: '',
  stepDetail: [],
  linkedHazardCategories: [],
});

const DEFAULT_PPE = () => ({
  id: crypto.randomUUID?.() ?? `p-${Date.now()}-${Math.random().toString(36).slice(2)}`,
  item: '',
  required: true,
  sortOrder: 0,
});

const DEFAULT_TOOL = () => ({
  id: crypto.randomUUID?.() ?? `t-${Date.now()}-${Math.random().toString(36).slice(2)}`,
  item: '',
  sortOrder: 0,
});

const DEFAULT_PERMIT = () => ({
  id: crypto.randomUUID?.() ?? `perm-${Date.now()}-${Math.random().toString(36).slice(2)}`,
  permitType: '',
  required: true,
  notes: '',
  sortOrder: 0,
});

const COMPLIANCE_DEFAULT = `This risk assessment and method statement has been prepared for the work described. The duty holder remains responsible for ensuring it is suitable for the specific site and circumstances and that all personnel have been instructed accordingly.`;

const AI_DISCLAIMER = `Any AI-generated content is draft or suggestions only and does not replace the duty holder or competent person responsibility.`;

export default function RamsEditor() {
  const { id } = useParams();
  const navigate = useNavigate();
  const isNew = !id || id === 'new';

  const [loading, setLoading] = useState(!isNew);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState('');
  const [form, setForm] = useState({
    title: '',
    description: '',
    linkEntityType: '',
    linkEntityId: '',
    siteSnapshot: {},
    complianceText: COMPLIANCE_DEFAULT,
    emergencyArrangements: '',
    hazards: [],
    methodSteps: [],
    ppe: [],
    tools: [],
    permits: [],
    status: 'draft',
    version: 1,
    supersededById: null,
    approvedBy: null,
    approvedAt: null,
    approvedByEmail: null,
  });
  const [statusTransitioning, setStatusTransitioning] = useState(false);
  const [aiSlideOverOpen, setAiSlideOverOpen] = useState(false);
  const [aiSlideOverOperation, setAiSlideOverOperation] = useState('build_hazards');
  const [aiSlideOverHazardIndex, setAiSlideOverHazardIndex] = useState(null);

  const loadRams = useCallback(async () => {
    if (isNew) return;
    try {
      setLoading(true);
      setError('');
      const data = await ramsAPI.getOne(id);
      setForm({
        title: data.title ?? '',
        description: data.description ?? '',
        linkEntityType: data.linkEntityType ?? '',
        linkEntityId: data.linkEntityId ?? '',
        siteSnapshot: data.siteSnapshot && typeof data.siteSnapshot === 'object' ? data.siteSnapshot : {},
        complianceText: data.complianceText ?? COMPLIANCE_DEFAULT,
        emergencyArrangements: data.emergencyArrangements ?? '',
        status: data.status ?? 'draft',
        version: data.version ?? 1,
        supersededById: data.supersededById ?? null,
        approvedBy: data.approvedBy ?? null,
        approvedAt: data.approvedAt ?? null,
        approvedByEmail: data.approvedByEmail ?? null,
        hazards: (data.hazards || []).map((h) => ({
          id: h.id,
          hazardCategory: h.hazardCategory ?? '',
          hazardText: h.hazardText ?? '',
          personsAtRisk: Array.isArray(h.personsAtRisk) ? h.personsAtRisk : [],
          initialLikelihood: h.initialLikelihood ?? null,
          initialSeverity: h.initialSeverity ?? null,
          initialRisk: h.initialRisk ?? null,
          residualLikelihood: h.residualLikelihood ?? null,
          residualSeverity: h.residualSeverity ?? null,
          residualRisk: h.residualRisk ?? null,
          controlMeasures: Array.isArray(h.controlMeasures)
            ? h.controlMeasures.map((c) => (typeof c === 'string' ? { text: c } : c))
            : [],
          sortOrder: h.sortOrder ?? 0,
        })),
        methodSteps: (data.methodSteps || []).map((s) => ({
          id: s.id,
          stepOrder: s.stepOrder ?? 0,
          stepTitle: s.stepTitle ?? '',
          stepDetail: Array.isArray(s.stepDetail) ? s.stepDetail : [],
          linkedHazardCategories: Array.isArray(s.linkedHazardCategories) ? s.linkedHazardCategories : [],
        })),
        ppe: (data.ppe || []).map((p) => ({
          id: p.id,
          item: p.item ?? '',
          required: p.required !== false,
          sortOrder: p.sortOrder ?? 0,
        })),
        tools: (data.tools || []).map((t) => ({
          id: t.id,
          item: t.item ?? '',
          sortOrder: t.sortOrder ?? 0,
        })),
        permits: (data.permits || []).map((p) => ({
          id: p.id,
          permitType: p.permitType ?? '',
          required: p.required !== false,
          notes: p.notes ?? '',
          sortOrder: p.sortOrder ?? 0,
        })),
      });
    } catch (err) {
      setError(err.message || 'Failed to load RAMS');
    } finally {
      setLoading(false);
    }
  }, [id, isNew]);

  useEffect(() => {
    loadRams();
  }, [loadRams]);

  const updateForm = (fn) => {
    setForm((prev) => (typeof fn === 'function' ? fn(prev) : { ...prev, ...fn }));
  };

  const setHazard = (index, patch) => {
    updateForm((prev) => {
      const next = [...(prev.hazards || [])];
      next[index] = { ...next[index], ...patch };
      const L = next[index].initialLikelihood;
      const S = next[index].initialSeverity;
      if (L != null && S != null) next[index].initialRisk = calcRisk(L, S);
      const rL = next[index].residualLikelihood;
      const rS = next[index].residualSeverity;
      if (rL != null && rS != null) next[index].residualRisk = calcRisk(rL, rS);
      return { ...prev, hazards: next };
    });
  };

  const addHazard = () => {
    updateForm((prev) => ({
      ...prev,
      hazards: [...(prev.hazards || []), { ...DEFAULT_HAZARD(), sortOrder: (prev.hazards?.length ?? 0) }],
    }));
  };

  const removeHazard = (index) => {
    updateForm((prev) => ({
      ...prev,
      hazards: prev.hazards.filter((_, i) => i !== index),
    }));
  };

  const addControlMeasure = (hazardIndex) => {
    setHazard(hazardIndex, {
      controlMeasures: [...(form.hazards[hazardIndex]?.controlMeasures || []), { text: '' }],
    });
  };

  const setControlMeasure = (hazardIndex, cmIndex, text) => {
    const cms = [...(form.hazards[hazardIndex]?.controlMeasures || [])];
    const existing = cms[cmIndex];
    cms[cmIndex] = existing && typeof existing === 'object' ? { ...existing, text } : { text };
    setHazard(hazardIndex, { controlMeasures: cms });
  };

  const removeControlMeasure = (hazardIndex, cmIndex) => {
    const cms = form.hazards[hazardIndex]?.controlMeasures?.filter((_, i) => i !== cmIndex) ?? [];
    setHazard(hazardIndex, { controlMeasures: cms });
  };

  const addMethodStep = () => {
    updateForm((prev) => ({
      ...prev,
      methodSteps: [
        ...(prev.methodSteps || []),
        { ...DEFAULT_METHOD_STEP(), stepOrder: prev.methodSteps?.length ?? 0 },
      ],
    }));
  };

  const setMethodStep = (index, patch) => {
    updateForm((prev) => {
      const next = [...(prev.methodSteps || [])];
      next[index] = { ...next[index], ...patch };
      return { ...prev, methodSteps: next };
    });
  };

  const removeMethodStep = (index) => {
    updateForm((prev) => ({
      ...prev,
      methodSteps: prev.methodSteps.filter((_, i) => i !== index),
    }));
  };

  const addStepDetailLine = (stepIndex) => {
    const step = form.methodSteps[stepIndex];
    const detail = [...(step?.stepDetail || []), ''];
    setMethodStep(stepIndex, { stepDetail: detail });
  };

  const setStepDetailLine = (stepIndex, lineIndex, value) => {
    const step = form.methodSteps[stepIndex];
    const detail = [...(step?.stepDetail || [])];
    detail[lineIndex] = value;
    setMethodStep(stepIndex, { stepDetail: detail });
  };

  const removeStepDetailLine = (stepIndex, lineIndex) => {
    const step = form.methodSteps[stepIndex];
    const detail = step?.stepDetail?.filter((_, i) => i !== lineIndex) ?? [];
    setMethodStep(stepIndex, { stepDetail: detail });
  };

  const addPpe = () => {
    updateForm((prev) => ({
      ...prev,
      ppe: [...(prev.ppe || []), { ...DEFAULT_PPE(), sortOrder: prev.ppe?.length ?? 0 }],
    }));
  };

  const setPpe = (index, patch) => {
    updateForm((prev) => {
      const next = [...(prev.ppe || [])];
      next[index] = { ...next[index], ...patch };
      return { ...prev, ppe: next };
    });
  };

  const removePpe = (index) => {
    updateForm((prev) => ({ ...prev, ppe: prev.ppe.filter((_, i) => i !== index) }));
  };

  const addTool = () => {
    updateForm((prev) => ({
      ...prev,
      tools: [...(prev.tools || []), { ...DEFAULT_TOOL(), sortOrder: prev.tools?.length ?? 0 }],
    }));
  };

  const setTool = (index, patch) => {
    updateForm((prev) => {
      const next = [...(prev.tools || [])];
      next[index] = { ...next[index], ...patch };
      return { ...prev, tools: next };
    });
  };

  const removeTool = (index) => {
    updateForm((prev) => ({ ...prev, tools: prev.tools.filter((_, i) => i !== index) }));
  };

  const addPermit = () => {
    updateForm((prev) => ({
      ...prev,
      permits: [...(prev.permits || []), { ...DEFAULT_PERMIT(), sortOrder: prev.permits?.length ?? 0 }],
    }));
  };

  const setPermit = (index, patch) => {
    updateForm((prev) => {
      const next = [...(prev.permits || [])];
      next[index] = { ...next[index], ...patch };
      return { ...prev, permits: next };
    });
  };

  const removePermit = (index) => {
    updateForm((prev) => ({ ...prev, permits: prev.permits.filter((_, i) => i !== index) }));
  };

  const validate = () => {
    if (!form.title?.trim()) {
      setError('Title is required');
      return false;
    }
    for (let i = 0; i < (form.hazards || []).length; i++) {
      const h = form.hazards[i];
      if (h.initialLikelihood != null && (h.initialLikelihood < 1 || h.initialLikelihood > 5)) {
        setError(`Hazard ${i + 1}: Initial likelihood must be 1–5`);
        return false;
      }
      if (h.initialSeverity != null && (h.initialSeverity < 1 || h.initialSeverity > 5)) {
        setError(`Hazard ${i + 1}: Initial severity must be 1–5`);
        return false;
      }
      if (h.residualLikelihood != null && (h.residualLikelihood < 1 || h.residualLikelihood > 5)) {
        setError(`Hazard ${i + 1}: Residual likelihood must be 1–5`);
        return false;
      }
      if (h.residualSeverity != null && (h.residualSeverity < 1 || h.residualSeverity > 5)) {
        setError(`Hazard ${i + 1}: Residual severity must be 1–5`);
        return false;
      }
    }
    setError('');
    return true;
  };

  const buildPayload = () => {
    const hazards = (form.hazards || []).map((h, i) => ({
      hazardCategory: h.hazardCategory || '',
      hazardText: h.hazardText || '',
      personsAtRisk: Array.isArray(h.personsAtRisk) ? h.personsAtRisk : [],
      initialLikelihood: h.initialLikelihood ?? null,
      initialSeverity: h.initialSeverity ?? null,
      initialRisk: h.initialRisk ?? null,
      residualLikelihood: h.residualLikelihood ?? null,
      residualSeverity: h.residualSeverity ?? null,
      residualRisk: h.residualRisk ?? null,
      controlMeasures: (h.controlMeasures || []).map((c) =>
        typeof c === 'object' && c?.text != null ? c : { text: String(c) }
      ),
      sortOrder: i,
    }));
    const methodSteps = (form.methodSteps || []).map((s, i) => ({
      stepOrder: i,
      stepTitle: s.stepTitle || '',
      stepDetail: Array.isArray(s.stepDetail) ? s.stepDetail : [],
      linkedHazardCategories: Array.isArray(s.linkedHazardCategories) ? s.linkedHazardCategories : [],
    }));
    const ppe = (form.ppe || []).map((p, i) => ({
      item: p.item || '',
      required: p.required !== false,
      sortOrder: i,
    }));
    const tools = (form.tools || []).map((t, i) => ({
      item: t.item || '',
      sortOrder: i,
    }));
    const permits = (form.permits || []).map((p, i) => ({
      permitType: p.permitType || '',
      required: p.required !== false,
      notes: p.notes || '',
      sortOrder: i,
    }));
    return {
      title: form.title.trim(),
      description: form.description?.trim() || null,
      linkEntityType: form.linkEntityType || null,
      linkEntityId: form.linkEntityId || null,
      siteSnapshot: form.siteSnapshot && typeof form.siteSnapshot === 'object' ? form.siteSnapshot : {},
      complianceText: form.complianceText?.trim() || null,
      emergencyArrangements: form.emergencyArrangements?.trim() || null,
      hazards,
      methodSteps,
      ppe,
      tools,
      permits,
    };
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!validate()) return;
    try {
      setSaving(true);
      setError('');
      const payload = buildPayload();
      if (isNew) {
        const created = await ramsAPI.create(payload);
        navigate(`/tenant/tools/rams/${created.id}/edit`, { replace: true });
      } else {
        await ramsAPI.update(id, payload);
        navigate(`/tenant/tools/rams/${id}`);
      }
    } catch (err) {
      setError(err.message || 'Failed to save RAMS');
    } finally {
      setSaving(false);
    }
  };

  const handleStatusTransition = async (newStatus) => {
    if (!id || isNew) return;
    try {
      setStatusTransitioning(true);
      setError('');
      await ramsAPI.updateStatus(id, newStatus);
      await loadRams();
    } catch (err) {
      setError(err.message || 'Failed to update status');
    } finally {
      setStatusTransitioning(false);
    }
  };

  const handleNewVersion = async () => {
    if (!id || isNew) return;
    try {
      setStatusTransitioning(true);
      setError('');
      const created = await ramsAPI.newVersion(id);
      if (created?.id) navigate(`/tenant/tools/rams/${created.id}/edit`, { replace: true });
    } catch (err) {
      setError(err.message || 'Failed to create new version');
    } finally {
      setStatusTransitioning(false);
    }
  };

  const openAISlideOver = (operation = 'build_hazards', hazardIndex = null) => {
    setAiSlideOverOperation(operation);
    setAiSlideOverHazardIndex(hazardIndex);
    setAiSlideOverOpen(true);
  };

  const handleAcceptHazards = (hazardsToAdd) => {
    if (!Array.isArray(hazardsToAdd) || hazardsToAdd.length === 0) return;
    updateForm((prev) => ({
      ...prev,
      hazards: [...(prev.hazards || []), ...hazardsToAdd],
    }));
  };

  const handleAcceptMethodSteps = (stepsToAdd) => {
    if (!Array.isArray(stepsToAdd) || stepsToAdd.length === 0) return;
    updateForm((prev) => ({
      ...prev,
      methodSteps: [...(prev.methodSteps || []), ...stepsToAdd],
    }));
  };

  const handleAcceptImproveControls = (hazardIndex, data) => {
    const improvedMeasures = (data.improved_control_measures || []).map((c) =>
      typeof c === 'object' && c?.text != null ? { text: c.text } : { text: String(c) }
    );
    setHazard(hazardIndex, {
      controlMeasures: improvedMeasures,
      residualLikelihood: data.residual_likelihood ?? null,
      residualSeverity: data.residual_severity ?? null,
      residualRisk: data.residual_risk ?? null,
    });
  };

  const handleAcceptPpeToolsPermits = (payload) => {
    if (payload.ppe?.length) {
      updateForm((prev) => ({
        ...prev,
        ppe: [...(prev.ppe || []), ...payload.ppe],
      }));
    }
    if (payload.tools?.length) {
      updateForm((prev) => ({
        ...prev,
        tools: [...(prev.tools || []), ...payload.tools],
      }));
    }
    if (payload.permits?.length) {
      updateForm((prev) => ({
        ...prev,
        permits: [...(prev.permits || []), ...payload.permits],
      }));
    }
    if (payload.emergency != null && payload.emergency !== '') {
      updateForm((prev) => ({
        ...prev,
        emergencyArrangements: (prev.emergencyArrangements ?? '') + (prev.emergencyArrangements ? '\n\n' : '') + payload.emergency,
      }));
    }
  };

  if (loading) {
    return (
      <Layout>
        <div className="max-w-5xl mx-auto px-6 py-8">
          <div className="flex items-center justify-center py-16">
            <div className="inline-block h-8 w-8 animate-spin rounded-full border-2 border-gray-300 dark:border-gray-600 border-t-blue-600" />
            <span className="ml-3 text-sm text-gray-600 dark:text-gray-400">Loading...</span>
          </div>
        </div>
      </Layout>
    );
  }

  if (!isNew && error && !form.title) {
    return (
      <Layout>
        <div className="max-w-5xl mx-auto px-6 py-8">
          <div className="rounded-md bg-red-50 dark:bg-red-900/20 border border-red-200 dark:border-red-800 p-4">
            <p className="text-sm text-red-700 dark:text-red-300">{error}</p>
            <Button variant="outline" size="sm" className="mt-3" onClick={() => navigate('/tenant/tools/rams')}>
              <ArrowLeft className="h-4 w-4 mr-1.5" />
              Back to RAMS
            </Button>
          </div>
        </div>
      </Layout>
    );
  }

  return (
    <Layout>
      <div className="bg-white dark:bg-gray-800 border-b border-gray-200 dark:border-gray-700">
        <div className="max-w-5xl mx-auto px-6 py-4">
          <div className="flex flex-wrap items-center justify-between gap-4">
            <div className="flex items-center gap-3 min-w-0">
              <Button variant="outline" size="sm" onClick={() => navigate('/tenant/tools/rams')}>
                <ArrowLeft className="h-4 w-4" />
              </Button>
              <div>
                <h1 className="text-xl font-bold text-gray-900 dark:text-gray-100">
                  {isNew ? 'New RAMS' : 'Edit RAMS'}
                </h1>
                {!isNew && (
                  <div className="flex flex-wrap items-center gap-2 mt-1">
                    <span className="text-sm text-gray-600 dark:text-gray-400">
                      Version {form.version ?? 1}
                    </span>
                    <Badge variant={form.status === 'approved' ? 'success' : 'neutral'}>
                      {STATUS_LABELS[form.status] ?? form.status}
                    </Badge>
                    {form.status === 'approved' && form.approvedAt && (
                      <span className="text-xs text-gray-500 dark:text-gray-400">
                        Approved {form.approvedByEmail ? `by ${form.approvedByEmail} ` : ''}
                        on {format(new Date(form.approvedAt), 'dd/MM/yyyy')}
                      </span>
                    )}
                  </div>
                )}
              </div>
            </div>
            {!isNew && (
              <div className="flex flex-wrap items-center gap-2">
                {(STATUS_TRANSITIONS[form.status] ?? []).map((t) => (
                  <Button
                    key={t.value}
                    type="button"
                    variant={t.value === 'approved' ? 'primary' : 'outline'}
                    size="sm"
                    onClick={() => handleStatusTransition(t.value)}
                    disabled={statusTransitioning}
                  >
                    {t.label}
                  </Button>
                ))}
                <Button
                  type="button"
                  variant="outline"
                  size="sm"
                  onClick={handleNewVersion}
                  disabled={statusTransitioning}
                >
                  <GitBranch className="h-4 w-4 mr-1.5" />
                  New version
                </Button>
              </div>
            )}
            <Button
              type="button"
              variant="outline"
              size="sm"
              onClick={() => openAISlideOver('build_hazards')}
            >
              <Sparkles className="h-4 w-4 mr-1.5" />
              AI assist
            </Button>
          </div>
        </div>
      </div>

      <RamsAISlideOver
        open={aiSlideOverOpen}
        onOpenChange={setAiSlideOverOpen}
        form={form}
        ramsId={isNew ? undefined : id}
        initialOperation={aiSlideOverOperation}
        improveControlsHazardIndex={aiSlideOverHazardIndex}
        onAcceptHazards={handleAcceptHazards}
        onAcceptMethodSteps={handleAcceptMethodSteps}
        onAcceptImproveControls={handleAcceptImproveControls}
        onAcceptPpeToolsPermits={handleAcceptPpeToolsPermits}
      />

      <form onSubmit={handleSubmit} className="max-w-5xl mx-auto px-6 py-8 space-y-8">
        {error && (
          <div className="rounded-md bg-red-50 dark:bg-red-900/20 border border-red-200 dark:border-red-800 p-4">
            <p className="text-sm text-red-700 dark:text-red-300">{error}</p>
          </div>
        )}

        {/* Header */}
        <Card className="bg-white dark:bg-gray-800 border-gray-200 dark:border-gray-700">
          <CardContent className="p-6">
            <h2 className="text-sm font-semibold text-gray-900 dark:text-gray-100 border-b border-gray-200 dark:border-gray-700 pb-2 mb-4">
              Header
            </h2>
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              <div className="md:col-span-2">
                <FormLabel className="text-gray-700 dark:text-gray-300">Title *</FormLabel>
                <Input
                  value={form.title}
                  onChange={(e) => updateForm({ title: e.target.value })}
                  className="border-gray-300 dark:border-gray-600 rounded-md bg-white dark:bg-gray-700 text-gray-900 dark:text-gray-100"
                  placeholder="RAMS title"
                  required
                />
              </div>
              <div className="md:col-span-2">
                <FormLabel className="text-gray-700 dark:text-gray-300">Description</FormLabel>
                <Textarea
                  value={form.description}
                  onChange={(e) => updateForm({ description: e.target.value })}
                  className="border-gray-300 dark:border-gray-600 rounded-md bg-white dark:bg-gray-700 text-gray-900 dark:text-gray-100 min-h-[80px]"
                  placeholder="Brief description"
                />
              </div>
              <div>
                <FormLabel className="text-gray-700 dark:text-gray-300">Link type</FormLabel>
                <Select
                  value={form.linkEntityType}
                  onChange={(e) => updateForm({ linkEntityType: e.target.value })}
                  className="border-gray-300 dark:border-gray-600 rounded-md bg-white dark:bg-gray-700 text-gray-900 dark:text-gray-100"
                >
                  <option value="">None</option>
                  <option value="job">Job</option>
                  <option value="invoice">Invoice</option>
                  <option value="estimate">Estimate</option>
                </Select>
              </div>
              <div>
                <FormLabel className="text-gray-700 dark:text-gray-300">Site name (snapshot)</FormLabel>
                <Input
                  value={form.siteSnapshot?.site_name ?? ''}
                  onChange={(e) =>
                    updateForm({
                      siteSnapshot: { ...form.siteSnapshot, site_name: e.target.value },
                    })
                  }
                  className="border-gray-300 dark:border-gray-600 rounded-md bg-white dark:bg-gray-700 text-gray-900 dark:text-gray-100"
                  placeholder="Site name"
                />
              </div>
              <div className="md:col-span-2">
                <FormLabel className="text-gray-700 dark:text-gray-300">Client name (snapshot)</FormLabel>
                <Input
                  value={form.siteSnapshot?.client_name ?? ''}
                  onChange={(e) =>
                    updateForm({
                      siteSnapshot: { ...form.siteSnapshot, client_name: e.target.value },
                    })
                  }
                  className="border-gray-300 dark:border-gray-600 rounded-md bg-white dark:bg-gray-700 text-gray-900 dark:text-gray-100"
                  placeholder="Client name"
                />
              </div>
            </div>
          </CardContent>
        </Card>

        {/* Compliance and disclaimers */}
        <Card className="bg-white dark:bg-gray-800 border-gray-200 dark:border-gray-700">
          <CardContent className="p-6">
            <h2 className="text-sm font-semibold text-gray-900 dark:text-gray-100 border-b border-gray-200 dark:border-gray-700 pb-2 mb-4">
              UK compliance and disclaimers
            </h2>
            <p className="text-xs text-gray-500 dark:text-gray-400 mb-3">{AI_DISCLAIMER}</p>
            <FormLabel className="text-gray-700 dark:text-gray-300">Compliance text</FormLabel>
            <Textarea
              value={form.complianceText}
              onChange={(e) => updateForm({ complianceText: e.target.value })}
              className="border-gray-300 dark:border-gray-600 rounded-md bg-white dark:bg-gray-700 text-gray-900 dark:text-gray-100 min-h-[120px]"
              placeholder="UK compliance and duty holder wording"
            />
          </CardContent>
        </Card>

        {/* Risk assessment */}
        <Card className="bg-white dark:bg-gray-800 border-gray-200 dark:border-gray-700">
          <CardContent className="p-6">
            <div className="flex items-center justify-between mb-4">
              <h2 className="text-sm font-semibold text-gray-900 dark:text-gray-100 border-b border-transparent pb-2">
                Risk assessment
              </h2>
              <Button type="button" variant="outline" size="sm" onClick={addHazard}>
                <Plus className="h-4 w-4 mr-1.5" />
                Add hazard
              </Button>
            </div>
            <p className="text-xs text-gray-500 dark:text-gray-400 mb-3">
              Likelihood and severity 1–5; risk = L × S (1–25).
            </p>
            {(!form.hazards || form.hazards.length === 0) ? (
              <p className="text-sm text-gray-500 dark:text-gray-400">No hazards. Add one above.</p>
            ) : (
              <div className="space-y-6">
                {form.hazards.map((h, hi) => (
                  <div
                    key={h.id}
                    className="p-4 rounded-lg border border-gray-200 dark:border-gray-600 bg-gray-50 dark:bg-gray-700/30 space-y-3"
                  >
                    <div className="flex justify-between items-center">
                      <span className="text-xs font-medium text-gray-500 dark:text-gray-400">
                        Hazard {hi + 1}
                      </span>
                      <div className="flex items-center gap-1">
                        <Button
                          type="button"
                          variant="ghost"
                          size="sm"
                          onClick={() => openAISlideOver('improve_controls', hi)}
                          className="text-gray-600 dark:text-gray-400"
                          title="Improve controls with AI"
                        >
                          <Wand2 className="h-4 w-4" />
                        </Button>
                        <Button
                          type="button"
                          variant="ghost"
                          size="sm"
                          onClick={() => removeHazard(hi)}
                          className="text-red-600 dark:text-red-400 h-8 w-8 p-0"
                        >
                          <Trash2 className="h-4 w-4" />
                        </Button>
                      </div>
                    </div>
                    <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
                      <div>
                        <FormLabel className="text-gray-700 dark:text-gray-300">Category</FormLabel>
                        <Select
                          value={h.hazardCategory}
                          onChange={(e) => setHazard(hi, { hazardCategory: e.target.value })}
                          className="border-gray-300 dark:border-gray-600 rounded-md bg-white dark:bg-gray-700 text-gray-900 dark:text-gray-100"
                        >
                          <option value="">Select...</option>
                          {HAZARD_CATEGORIES.map((c) => (
                            <option key={c} value={c}>{c}</option>
                          ))}
                        </Select>
                      </div>
                    </div>
                    <div>
                      <FormLabel className="text-gray-700 dark:text-gray-300">Hazard description</FormLabel>
                      <Textarea
                        value={h.hazardText}
                        onChange={(e) => setHazard(hi, { hazardText: e.target.value })}
                        className="border-gray-300 dark:border-gray-600 rounded-md bg-white dark:bg-gray-700 text-gray-900 dark:text-gray-100 min-h-[60px]"
                      />
                    </div>
                    <div>
                      <FormLabel className="text-gray-700 dark:text-gray-300">Persons at risk</FormLabel>
                      <Select
                        multiple
                        value={h.personsAtRisk || []}
                        onChange={(e) => {
                          const selected = Array.from(e.target.selectedOptions, (o) => o.value);
                          setHazard(hi, { personsAtRisk: selected });
                        }}
                        className="border-gray-300 dark:border-gray-600 rounded-md bg-white dark:bg-gray-700 text-gray-900 dark:text-gray-100 min-h-[80px]"
                      >
                        {PERSONS_AT_RISK.map((p) => (
                          <option key={p} value={p}>{p}</option>
                        ))}
                      </Select>
                    </div>
                    <div className="grid grid-cols-2 md:grid-cols-6 gap-2">
                      <div>
                        <FormLabel className="text-gray-700 dark:text-gray-300">Initial L</FormLabel>
                        <Select
                          value={h.initialLikelihood ?? ''}
                          onChange={(e) =>
                            setHazard(hi, {
                              initialLikelihood: e.target.value === '' ? null : Number(e.target.value),
                            })
                          }
                          className="border-gray-300 dark:border-gray-600 rounded-md bg-white dark:bg-gray-700 text-gray-900 dark:text-gray-100"
                        >
                          <option value="">—</option>
                          {LIKELIHOOD_SEVERITY_OPTS.map((o) => (
                            <option key={o.value} value={o.value}>{o.label}</option>
                          ))}
                        </Select>
                      </div>
                      <div>
                        <FormLabel className="text-gray-700 dark:text-gray-300">Initial S</FormLabel>
                        <Select
                          value={h.initialSeverity ?? ''}
                          onChange={(e) =>
                            setHazard(hi, {
                              initialSeverity: e.target.value === '' ? null : Number(e.target.value),
                            })
                          }
                          className="border-gray-300 dark:border-gray-600 rounded-md bg-white dark:bg-gray-700 text-gray-900 dark:text-gray-100"
                        >
                          <option value="">—</option>
                          {LIKELIHOOD_SEVERITY_OPTS.map((o) => (
                            <option key={o.value} value={o.value}>{o.label}</option>
                          ))}
                        </Select>
                      </div>
                      <div>
                        <FormLabel className="text-gray-700 dark:text-gray-300">Initial R</FormLabel>
                        <Input
                          type="text"
                          readOnly
                          value={h.initialRisk ?? '—'}
                          className="border-gray-300 dark:border-gray-600 rounded-md bg-gray-100 dark:bg-gray-700 text-gray-900 dark:text-gray-100"
                        />
                      </div>
                      <div>
                        <FormLabel className="text-gray-700 dark:text-gray-300">Residual L</FormLabel>
                        <Select
                          value={h.residualLikelihood ?? ''}
                          onChange={(e) =>
                            setHazard(hi, {
                              residualLikelihood: e.target.value === '' ? null : Number(e.target.value),
                            })
                          }
                          className="border-gray-300 dark:border-gray-600 rounded-md bg-white dark:bg-gray-700 text-gray-900 dark:text-gray-100"
                        >
                          <option value="">—</option>
                          {LIKELIHOOD_SEVERITY_OPTS.map((o) => (
                            <option key={o.value} value={o.value}>{o.label}</option>
                          ))}
                        </Select>
                      </div>
                      <div>
                        <FormLabel className="text-gray-700 dark:text-gray-300">Residual S</FormLabel>
                        <Select
                          value={h.residualSeverity ?? ''}
                          onChange={(e) =>
                            setHazard(hi, {
                              residualSeverity: e.target.value === '' ? null : Number(e.target.value),
                            })
                          }
                          className="border-gray-300 dark:border-gray-600 rounded-md bg-white dark:bg-gray-700 text-gray-900 dark:text-gray-100"
                        >
                          <option value="">—</option>
                          {LIKELIHOOD_SEVERITY_OPTS.map((o) => (
                            <option key={o.value} value={o.value}>{o.label}</option>
                          ))}
                        </Select>
                      </div>
                      <div>
                        <FormLabel className="text-gray-700 dark:text-gray-300">Residual R</FormLabel>
                        <Input
                          type="text"
                          readOnly
                          value={h.residualRisk ?? '—'}
                          className="border-gray-300 dark:border-gray-600 rounded-md bg-gray-100 dark:bg-gray-700 text-gray-900 dark:text-gray-100"
                        />
                      </div>
                    </div>
                    <div>
                      <div className="flex justify-between items-center mb-1">
                        <FormLabel className="text-gray-700 dark:text-gray-300">Control measures</FormLabel>
                        <Button
                          type="button"
                          variant="ghost"
                          size="sm"
                          onClick={() => addControlMeasure(hi)}
                        >
                          <Plus className="h-3 w-3 mr-1" />
                          Add
                        </Button>
                      </div>
                      <ul className="space-y-2">
                        {(h.controlMeasures || []).map((cm, ci) => (
                          <li key={ci} className="flex gap-2">
                            <Input
                              value={typeof cm === 'object' && cm?.text != null ? cm.text : String(cm)}
                              onChange={(e) => setControlMeasure(hi, ci, e.target.value)}
                              className="border-gray-300 dark:border-gray-600 rounded-md bg-white dark:bg-gray-700 text-gray-900 dark:text-gray-100 flex-1"
                              placeholder="Control measure"
                            />
                            <Button
                              type="button"
                              variant="ghost"
                              size="sm"
                              onClick={() => removeControlMeasure(hi, ci)}
                              className="text-red-600 dark:text-red-400 shrink-0"
                            >
                              <Trash2 className="h-4 w-4" />
                            </Button>
                          </li>
                        ))}
                      </ul>
                    </div>
                  </div>
                ))}
              </div>
            )}
          </CardContent>
        </Card>

        {/* Method statement */}
        <Card className="bg-white dark:bg-gray-800 border-gray-200 dark:border-gray-700">
          <CardContent className="p-6">
            <div className="flex items-center justify-between mb-4">
              <h2 className="text-sm font-semibold text-gray-900 dark:text-gray-100 border-b border-transparent pb-2">
                Method statement
              </h2>
              <Button type="button" variant="outline" size="sm" onClick={addMethodStep}>
                <Plus className="h-4 w-4 mr-1.5" />
                Add step
              </Button>
            </div>
            {(!form.methodSteps || form.methodSteps.length === 0) ? (
              <p className="text-sm text-gray-500 dark:text-gray-400">No steps. Add one above.</p>
            ) : (
              <ol className="list-decimal list-inside space-y-4">
                {form.methodSteps.map((step, si) => (
                  <li key={step.id} className="border border-gray-200 dark:border-gray-600 rounded-lg p-3 bg-gray-50 dark:bg-gray-700/30">
                    <div className="flex justify-between items-start gap-2">
                      <div className="flex-1 min-w-0">
                        <Input
                          value={step.stepTitle}
                          onChange={(e) => setMethodStep(si, { stepTitle: e.target.value })}
                          placeholder="Step title"
                          className="border-gray-300 dark:border-gray-600 rounded-md bg-white dark:bg-gray-700 text-gray-900 dark:text-gray-100"
                        />
                        <ul className="mt-2 ml-4 space-y-1">
                          {(step.stepDetail || []).map((line, li) => (
                            <li key={li} className="flex gap-2 items-center">
                              <Input
                                value={line}
                                onChange={(e) => setStepDetailLine(si, li, e.target.value)}
                                className="border-gray-300 dark:border-gray-600 rounded-md bg-white dark:bg-gray-700 text-gray-900 dark:text-gray-100 flex-1 text-sm"
                                placeholder="Detail"
                              />
                              <Button
                                type="button"
                                variant="ghost"
                                size="sm"
                                onClick={() => removeStepDetailLine(si, li)}
                                className="text-red-600 dark:text-red-400 shrink-0 h-8"
                              >
                                <Trash2 className="h-4 w-4" />
                              </Button>
                            </li>
                          ))}
                          <li>
                            <Button
                              type="button"
                              variant="ghost"
                              size="sm"
                              onClick={() => addStepDetailLine(si)}
                              className="text-xs"
                            >
                              <Plus className="h-3 w-3 mr-1" />
                              Add detail line
                            </Button>
                          </li>
                        </ul>
                      </div>
                      <Button
                        type="button"
                        variant="ghost"
                        size="sm"
                        onClick={() => removeMethodStep(si)}
                        className="text-red-600 dark:text-red-400 shrink-0"
                      >
                        <Trash2 className="h-4 w-4" />
                      </Button>
                    </div>
                  </li>
                ))}
              </ol>
            )}
          </CardContent>
        </Card>

        {/* PPE */}
        <Card className="bg-white dark:bg-gray-800 border-gray-200 dark:border-gray-700">
          <CardContent className="p-6">
            <div className="flex items-center justify-between mb-4">
              <h2 className="text-sm font-semibold text-gray-900 dark:text-gray-100 border-b border-transparent pb-2">
                PPE
              </h2>
              <Button type="button" variant="outline" size="sm" onClick={addPpe}>
                <Plus className="h-4 w-4 mr-1.5" />
                Add item
              </Button>
            </div>
            {(!form.ppe || form.ppe.length === 0) ? (
              <p className="text-sm text-gray-500 dark:text-gray-400">None listed.</p>
            ) : (
              <ul className="space-y-2">
                {form.ppe.map((p, pi) => (
                  <li key={p.id} className="flex gap-2 items-center">
                    <Input
                      value={p.item}
                      onChange={(e) => setPpe(pi, { item: e.target.value })}
                      placeholder="PPE item"
                      className="border-gray-300 dark:border-gray-600 rounded-md bg-white dark:bg-gray-700 text-gray-900 dark:text-gray-100 flex-1"
                    />
                    <label className="flex items-center gap-1.5 text-sm text-gray-700 dark:text-gray-300 shrink-0">
                      <input
                        type="checkbox"
                        checked={p.required !== false}
                        onChange={(e) => setPpe(pi, { required: e.target.checked })}
                        className="rounded border-gray-300 dark:border-gray-600 text-blue-600 focus:ring-blue-500"
                      />
                      Required
                    </label>
                    <Button
                      type="button"
                      variant="ghost"
                      size="sm"
                      onClick={() => removePpe(pi)}
                      className="text-red-600 dark:text-red-400 shrink-0"
                    >
                      <Trash2 className="h-4 w-4" />
                    </Button>
                  </li>
                ))}
              </ul>
            )}
          </CardContent>
        </Card>

        {/* Tools */}
        <Card className="bg-white dark:bg-gray-800 border-gray-200 dark:border-gray-700">
          <CardContent className="p-6">
            <div className="flex items-center justify-between mb-4">
              <h2 className="text-sm font-semibold text-gray-900 dark:text-gray-100 border-b border-transparent pb-2">
                Tools and equipment
              </h2>
              <Button type="button" variant="outline" size="sm" onClick={addTool}>
                <Plus className="h-4 w-4 mr-1.5" />
                Add item
              </Button>
            </div>
            {(!form.tools || form.tools.length === 0) ? (
              <p className="text-sm text-gray-500 dark:text-gray-400">None listed.</p>
            ) : (
              <ul className="space-y-2">
                {form.tools.map((t, ti) => (
                  <li key={t.id} className="flex gap-2 items-center">
                    <Input
                      value={t.item}
                      onChange={(e) => setTool(ti, { item: e.target.value })}
                      placeholder="Tool or equipment"
                      className="border-gray-300 dark:border-gray-600 rounded-md bg-white dark:bg-gray-700 text-gray-900 dark:text-gray-100 flex-1"
                    />
                    <Button
                      type="button"
                      variant="ghost"
                      size="sm"
                      onClick={() => removeTool(ti)}
                      className="text-red-600 dark:text-red-400 shrink-0"
                    >
                      <Trash2 className="h-4 w-4" />
                    </Button>
                  </li>
                ))}
              </ul>
            )}
          </CardContent>
        </Card>

        {/* Permits */}
        <Card className="bg-white dark:bg-gray-800 border-gray-200 dark:border-gray-700">
          <CardContent className="p-6">
            <div className="flex items-center justify-between mb-4">
              <h2 className="text-sm font-semibold text-gray-900 dark:text-gray-100 border-b border-transparent pb-2">
                Permits and authorisations
              </h2>
              <Button type="button" variant="outline" size="sm" onClick={addPermit}>
                <Plus className="h-4 w-4 mr-1.5" />
                Add permit
              </Button>
            </div>
            {(!form.permits || form.permits.length === 0) ? (
              <p className="text-sm text-gray-500 dark:text-gray-400">None listed.</p>
            ) : (
              <ul className="space-y-3">
                {form.permits.map((p, pi) => (
                  <li key={p.id} className="flex flex-wrap gap-2 items-start border border-gray-200 dark:border-gray-600 rounded-lg p-3 bg-gray-50 dark:bg-gray-700/30">
                    <Select
                      value={p.permitType}
                      onChange={(e) => setPermit(pi, { permitType: e.target.value })}
                      className="border-gray-300 dark:border-gray-600 rounded-md bg-white dark:bg-gray-700 text-gray-900 dark:text-gray-100 w-full md:w-48"
                    >
                      <option value="">Type...</option>
                      {PERMIT_TYPES.map((pt) => (
                        <option key={pt} value={pt}>{pt}</option>
                      ))}
                    </Select>
                    <label className="flex items-center gap-1.5 text-sm text-gray-700 dark:text-gray-300">
                      <input
                        type="checkbox"
                        checked={p.required !== false}
                        onChange={(e) => setPermit(pi, { required: e.target.checked })}
                        className="rounded border-gray-300 dark:border-gray-600 text-blue-600 focus:ring-blue-500"
                      />
                      Required
                    </label>
                    <Input
                      value={p.notes ?? ''}
                      onChange={(e) => setPermit(pi, { notes: e.target.value })}
                      placeholder="Notes"
                      className="border-gray-300 dark:border-gray-600 rounded-md bg-white dark:bg-gray-700 text-gray-900 dark:text-gray-100 flex-1 min-w-[120px]"
                    />
                    <Button
                      type="button"
                      variant="ghost"
                      size="sm"
                      onClick={() => removePermit(pi)}
                      className="text-red-600 dark:text-red-400 shrink-0"
                    >
                      <Trash2 className="h-4 w-4" />
                    </Button>
                  </li>
                ))}
              </ul>
            )}
          </CardContent>
        </Card>

        {/* Emergency arrangements */}
        <Card className="bg-white dark:bg-gray-800 border-gray-200 dark:border-gray-700">
          <CardContent className="p-6">
            <h2 className="text-sm font-semibold text-gray-900 dark:text-gray-100 border-b border-gray-200 dark:border-gray-700 pb-2 mb-4">
              Emergency arrangements
            </h2>
            <Textarea
              value={form.emergencyArrangements}
              onChange={(e) => updateForm({ emergencyArrangements: e.target.value })}
              className="border-gray-300 dark:border-gray-600 rounded-md bg-white dark:bg-gray-700 text-gray-900 dark:text-gray-100 min-h-[100px]"
              placeholder="Emergency procedures, contacts, first aid, assembly points"
            />
          </CardContent>
        </Card>

        {/* Sign-off note (read-only info; signatures are append-only on server) */}
        <Card className="bg-white dark:bg-gray-800 border-gray-200 dark:border-gray-700">
          <CardContent className="p-6">
            <h2 className="text-sm font-semibold text-gray-900 dark:text-gray-100 border-b border-gray-200 dark:border-gray-700 pb-2 mb-4">
              Sign-off
            </h2>
            <p className="text-xs text-gray-500 dark:text-gray-400">
              Sign-off and approvals are recorded when the document status is changed to Issued or Approved. View the RAMS to see signatures.
            </p>
          </CardContent>
        </Card>

        <div className="flex items-center gap-3 pt-4">
          <Button type="submit" variant="primary" disabled={saving}>
            {saving ? 'Saving...' : isNew ? 'Create RAMS' : 'Save'}
          </Button>
          <Button type="button" variant="outline" onClick={() => navigate('/tenant/tools/rams')}>
            Cancel
          </Button>
        </div>
      </form>
    </Layout>
  );
}
